import { useEffect, useRef, useState } from 'react';
import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
  type UserCredential,
} from 'firebase/auth';
import {
  initializeFirestore,
  doc,
  onSnapshot,
  type Firestore,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { getTokenCount } from '@/lib/tryon';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const auth = getAuth(app);

/** Firestore "common" database — token balance, transactions, config. Shared across Mirror and other services. */
export const commonDb: Firestore = initializeFirestore(app, {}, 'common');

// Google Sign-In uses the SwitchFit project's OAuth client (Firebase Console → Authentication → Google).
// client_id cannot be set in code; configure Web client ID in that project.
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export async function signInWithGoogle(popup = true): Promise<UserCredential> {
  return popup
    ? signInWithPopup(auth, googleProvider)
    : signInWithRedirect(auth, googleProvider);
}

export async function getRedirectAuthResult() {
  return getRedirectResult(auth);
}

export function signOut() {
  return firebaseSignOut(auth);
}

export function subscribeAuth(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

export function getCurrentUser(): User | null {
  return auth.currentUser;
}

export function useAuth(): { user: User | null; loading: boolean } {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  return { user, loading };
}

/** Result of listening to the current user's document in the mirror DB (tokens, etc.). */
export interface MirrorUserSnapshot {
  tokens: number | null;
  loading: boolean;
}

/**
 * Subscribe to the authenticated user's document in the common Firestore database (token balance).
 * Token count stays in sync (e.g. after generation or purchase) without polling or extension events.
 * If the user doc does not exist yet, we call getTokenCount to create it with INITIAL_TOKENS so the listener receives the new doc.
 */
export function useMirrorUser(): MirrorUserSnapshot {
  const { user } = useAuth();
  const [tokens, setTokens] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const triedInitRef = useRef(false);

  useEffect(() => {
    if (!user) {
      setTokens(null);
      setLoading(false);
      triedInitRef.current = false;
      return;
    }
    triedInitRef.current = false;
    setLoading(true);
    const userRef = doc(commonDb, 'users', user.uid);
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        const data = snap.data();
        if (snap.exists() && typeof data?.tokens === 'number') {
          setTokens(data.tokens);
        } else {
          setTokens(null);
          if (!triedInitRef.current) {
            triedInitRef.current = true;
            getTokenCount().catch(() => {
              triedInitRef.current = false;
            });
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error('Mirror user snapshot error', err);
        setTokens(null);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.uid]);

  return { tokens, loading };
}

/** Cloud Functions (same project as Mirror; admin callables are deployed from SwitchFit functions). */
let _functions: Functions | null = null;
export function getFunctionsInstance(): Functions | null {
  if (typeof window === 'undefined') return null;
  if (!_functions) {
    _functions = getFunctions(app);
    if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
      try {
        connectFunctionsEmulator(_functions, 'localhost', 5001);
      } catch {
        // ignore
      }
    }
  }
  return _functions;
}

export default app;
